import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";

export default function DisclaimerPage() {
  useEffect(() => {
    document.title = "Disclaimer — CFO Grade";
    const desc =
      "CFO Grade provides educational insights for business owners. It is not financial, tax, legal, or investment advice. Full terms of use.";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", desc);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}/disclaimer`);
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1A1A1A]" data-testid="disclaimer-page">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="disclaimer-home-link">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to home
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 pt-6 pb-16">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D]">Terms</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] leading-tight mt-3 tracking-tight">
          Disclaimer & Terms of Use
        </h1>
        <p className="text-[#5C665D] mt-3 text-sm">
          Last updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-[#1A1A1A]">
          <section>
            <h2 className="font-serif-display text-2xl text-[#1F382E] mb-3">What CFO Grade is</h2>
            <p>
              CFO Grade is an educational tool that takes the financial figures you enter and translates them
              into ratios, peer benchmarks, a letter grade, and a plain-English narrative. It is intended to
              help business owners, founders, operators, students, and analysts <em>better understand</em> the
              financial story their numbers tell.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl text-[#1F382E] mb-3">What CFO Grade is not</h2>
            <p>
              CFO Grade is <strong>not</strong> financial advice, investment advice, accounting advice, tax advice,
              legal advice, or a credit / underwriting decision. The outputs are not produced by a licensed
              accountant, CPA, CFA, fiduciary, or financial advisor, and no professional relationship is
              created by your use of this site.
            </p>
            <p className="mt-3">
              CFO Grade is not affiliated with, endorsed by, or sponsored by any bank, regulator, government
              body, accounting firm, or rating agency.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl text-[#1F382E] mb-3">Accuracy & limitations</h2>
            <p>
              The quality of every output depends entirely on the figures you provide. We do not audit, verify,
              or reconcile your inputs. Benchmarks shown on the site are typical mid-range estimates for the
              industries and regions listed and may not reflect your specific operating context, capital
              structure, business model, or local accounting standards. AI-generated narratives may contain
              errors, omissions, or hallucinations.
            </p>
            <p className="mt-3">
              You agree to independently verify any number, ratio, benchmark, or recommendation before relying
              on it for any business, lending, hiring, investment, or other decision.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl text-[#1F382E] mb-3">Use at your own risk</h2>
            <p>
              By using CFO Grade you agree that you do so at your own risk and that you will not rely on the
              outputs as a substitute for professional advice. To the maximum extent permitted by law, the
              operators of CFO Grade disclaim all warranties, express or implied, and shall not be liable for
              any direct, indirect, incidental, consequential, or punitive damages arising out of or in
              connection with your use of the site or its outputs.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl text-[#1F382E] mb-3">Privacy & data</h2>
            <p>
              The figures you enter are stored to generate your report and (where you opt in) to build
              anonymized industry benchmarks. We do not sell your data. Pro entitlements are bound to your
              device via a stored token; you can save a restore link or email it to yourself to recover access
              on another device.
            </p>
            <p className="mt-3">
              Upcoming premium features (e.g. CFO Books bank-statement upload) will store uploaded
              documents encrypted at rest and auto-delete them after 90 days unless you save them.
              Documents are never shared with third parties and are processed solely to reconstruct
              your financial statements for your own use.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl text-[#1F382E] mb-3">Payments</h2>
            <p>
              Paid features (Pro Pass, single-report PDF) are processed by Stripe. CFO Grade does not store or
              have access to your card details. Refunds are handled case-by-case via the contact channel below.
            </p>
          </section>

          <section>
            <h2 className="font-serif-display text-2xl text-[#1F382E] mb-3">Contact</h2>
            <p>
              Questions, corrections, or refund requests —{" "}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("cfograde:open-feedback"))}
                className="text-[#1F382E] underline underline-offset-4 hover:text-[#C26D5C] transition-colors"
                data-testid="disclaimer-open-feedback"
              >
                send us a note here
              </button>
              . We read everything that comes in. Anonymous is fine; leave a way
              to reach you in the message if you want a reply on a refund.
            </p>
          </section>

          <section className="border-t border-[#E5E0D8] pt-6">
            <p className="text-sm text-[#5C665D]">
              By continuing to use this site, you acknowledge that you have read, understood, and agreed to
              these terms.
            </p>
          </section>
        </div>
      </main>

      <SiteFootnote />
    </div>
  );
}
